import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { AppContext } from '../../../contexts/AppContext';
import api from '../../../services/api';
import { Grid, Paper, Text, Button } from '../../core';
import { SeccionHeader, ConfirmDialog, AlertDialog, LineDetails } from '../../patterns';

const STATUS_LABELS = {
  draft: 'Rascunho',
  scheduled: 'Agendada',
  sending: 'Enviando',
  sent: 'Enviada',
  failed: 'Falhou',
  cancelled: 'Cancelada'
};

const MESSAGE_TYPE_LABELS = {
  content: 'Conteúdo',
  reengagement: 'Reengajamento',
  operational: 'Operacional'
};

const AUDIENCE_LABELS = {
  all: 'Todos os usuários',
  user: 'Usuário específico',
  users: 'Seleção múltipla',
  group: 'Grupo salvo',
  segment: 'Segmento dinâmico'
};

const SCREEN_LABELS = {
  LessonDetails: 'Detalhe da Lição',
  LessonsList: 'Lista de Lições',
  CyclesList: 'Lista de Ciclos',
  CycleList: 'Lista de Ciclos',
  MainTabNavigator: 'Aba do App'
};

export default function NotificationDetails() {
  const { setLoading } = useContext(AppContext);
  const { id } = useParams();
  const history = useHistory();
  const pollingRef = useRef(null);

  const [notification, setNotification] = useState(null);
  const [groupName, setGroupName] = useState(null);
  const [sendProgress, setSendProgress] = useState(null);
  const [state, setState] = useState({ showConfirmDialog: false, showCancelDialog: false });

  const [alert, setAlert] = useState({
    show: false,
    errors: [{ message: 'Ocorreu um erro' }]
  });

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const loadNotification = useCallback(() => {
    setLoading(true);
    return api
      .get(`/admin/push-notifications/${id}`)
      .then((res) => {
        const data = res.data.pushnotification;
        setNotification(data);
        if (data.sendProgress) {
          setSendProgress(data.sendProgress);
        }
        if (data.audience?.type === 'group' && data.audience?.groupId) {
          api.get(`/admin/user-groups/${data.audience.groupId}`)
            .then((groupRes) => setGroupName(groupRes.data.usergroup?.name || null))
            .catch(() => setGroupName(null));
        } else {
          setGroupName(null);
        }
        setLoading(false);
        return data;
      })
      .catch((err) => {
        setLoading(false);
        setAlert({
          errors: err.response?.data?.errors || [{ message: 'Erro ao carregar notificação' }],
          show: true
        });
        history.push('/app/notifications');
        return null;
      });
  }, [id, setLoading, history]);

  const startStatusPolling = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      api
        .get(`/admin/push-notifications/${id}/status`)
        .then((res) => {
          const { status, sendProgress: progress, stats } = res.data;
          if (progress) {
            setSendProgress(progress);
          }
          if (status !== 'sending') {
            stopPolling();
            setNotification((prev) => prev ? { ...prev, status, stats, sendProgress: progress } : prev);
          }
        })
        .catch(() => stopPolling());
    }, 2000);
  }, [id, stopPolling]);

  useEffect(() => {
    loadNotification().then((data) => {
      if (data?.status === 'sending') {
        startStatusPolling();
      }
    });
    return () => stopPolling();
  }, [loadNotification, startStatusPolling, stopPolling]);

  const handleEditButton = () => {
    if (notification?.id) {
      history.push(`/app/notifications/edit/${notification.id}`);
    }
  };

  const handleDeleteButton = () => {
    if (notification?.id) {
      api
        .delete(`/admin/push-notifications/${notification.id}`)
        .then(() => history.push('/app/notifications'))
        .catch((err) => {
          setAlert({
            errors: err.response?.data?.errors || [{ message: 'Erro ao excluir notificação' }],
            show: true
          });
        });
    }
  };

  const handleCancelScheduled = () => {
    if (notification?.id) {
      setLoading(true);
      api
        .post(`/admin/push-notifications/${notification.id}/cancel`)
        .then(() => loadNotification())
        .catch((err) => {
          setLoading(false);
          setAlert({
            errors: err.response?.data?.errors || [{ message: 'Erro ao cancelar agendamento' }],
            show: true
          });
        });
    }
  };

  const formatSegmentCriteria = (segment) => {
    if (!segment) return '—';
    const parts = [];
    if (segment.language) parts.push(`Idioma: ${segment.language}`);
    if (segment.speaker) parts.push('Speaker');
    if (segment.inactiveDays != null) parts.push(`Inativos ≥ ${segment.inactiveDays} dias`);
    if (segment.country) parts.push(`País: ${segment.country}`);
    if (segment.state) parts.push(`Estado: ${segment.state}`);
    if (segment.city) parts.push(`Cidade: ${segment.city}`);
    if (segment.hasCompletedLessons === false) parts.push('Sem lições concluídas');
    if (segment.hasCompletedLessons === true) parts.push('Com lições concluídas');
    return parts.length ? parts.join(' · ') : 'Sem critérios específicos';
  };

  const getAudienceDetail = () => {
    if (!notification?.audience) return '—';
    const typeLabel = AUDIENCE_LABELS[notification.audience.type] || notification.audience.type;
    if (notification.audience.type === 'group') {
      return `${typeLabel}${groupName ? `: ${groupName}` : ''}`;
    }
    if (notification.audience.type === 'segment') {
      return `${typeLabel} — ${formatSegmentCriteria(notification.audience.segment)}`;
    }
    return typeLabel;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  const canEdit = notification && ['draft', 'scheduled'].includes(notification.status);
  const canDelete = notification && notification.status === 'draft';
  const canCancelSchedule = notification && notification.status === 'scheduled';

  if (!notification) {
    return null;
  }

  return (
    <>
      <Grid container>
        <SeccionHeader
          title={notification.title?.pt || 'Notificação'}
          editButton={canEdit}
          editButtonAction={handleEditButton}
          deleteButton={canDelete}
          deleteButtonAction={() => setState({ showConfirmDialog: true })}
        />
        <AlertDialog
          open={alert.show}
          handleClose={() => setAlert({ ...alert, show: false })}
          errors={alert.errors}
        />

        {notification.status === 'sending' && sendProgress && (
          <Grid item xs={12}>
            <Paper style={{ padding: 16, marginBottom: 16, backgroundColor: '#e3f2fd' }}>
              <Text style={{ fontWeight: 700 }}>Enviando notificação...</Text>
              <Text>
                Progresso: {sendProgress.processed}/{sendProgress.total}
              </Text>
            </Paper>
          </Grid>
        )}

        <Grid item xs={12}>
          <Paper style={{ flex: 1, display: 'flex', padding: 20 }}>
            <Grid container>
              <Grid item xs={12}>
                <Text variant="h6">Conteúdo</Text>
              </Grid>
              <LineDetails title="Título (PT)" text={notification.title?.pt || ''} />
              <LineDetails title="Título (EN)" text={notification.title?.en || ''} />
              <LineDetails title="Título (ES)" text={notification.title?.es || ''} />
              <LineDetails title="Corpo (PT)" text={notification.body?.pt || ''} />
              <LineDetails title="Corpo (EN)" text={notification.body?.en || ''} />
              <LineDetails title="Corpo (ES)" text={notification.body?.es || ''} />

              <Grid item xs={12} style={{ marginTop: 16 }}>
                <Text variant="h6">Configuração</Text>
              </Grid>
              <LineDetails
                title="Tipo"
                text={MESSAGE_TYPE_LABELS[notification.messageType] || notification.messageType}
              />
              <LineDetails
                title="Status"
                text={STATUS_LABELS[notification.status] || notification.status}
              />
              <LineDetails
                title="Destino"
                text={SCREEN_LABELS[notification.destination?.screen] || notification.destination?.screen || ''}
              />
              <LineDetails
                title="Audiência"
                text={getAudienceDetail()}
              />
              <LineDetails title="Criada em" text={formatDate(notification.createdAt)} />
              <LineDetails title="Agendada para" text={formatDate(notification.scheduledAt)} />
              <LineDetails title="Enviada em" text={formatDate(notification.sentAt)} />

              {notification.stats && (
                <>
                  <Grid item xs={12} style={{ marginTop: 16 }}>
                    <Text variant="h6">Métricas de entrega</Text>
                  </Grid>
                  <LineDetails title="Usuários elegíveis" text={String(notification.stats.eligibleUsers ?? 0)} />
                  <LineDetails title="Tokens elegíveis" text={String(notification.stats.eligibleTokens ?? 0)} />
                  <LineDetails title="Entregues" text={String(notification.stats.delivered ?? 0)} />
                  <LineDetails title="Falhas" text={String(notification.stats.failed ?? 0)} />
                  {(notification.stats.delivered != null && notification.stats.eligibleTokens > 0) && (
                    <LineDetails
                      title="Taxa de entrega"
                      text={`${Math.round((notification.stats.delivered / notification.stats.eligibleTokens) * 100)}%`}
                    />
                  )}
                  <LineDetails title="Aberturas" text={String(notification.stats.opened ?? 0)} />
                  {(notification.stats.delivered > 0) && (
                    <LineDetails
                      title="Taxa de abertura"
                      text={`${Math.round(((notification.stats.opened ?? 0) / notification.stats.delivered) * 100)}%`}
                    />
                  )}
                </>
              )}

              {canCancelSchedule && (
                <Grid item xs={12} style={{ marginTop: 16 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setState({ ...state, showCancelDialog: true })}
                  >
                    Cancelar agendamento
                  </Button>
                </Grid>
              )}

              {notification.errorMessage && (
                <LineDetails title="Erro" text={notification.errorMessage} />
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {state.showConfirmDialog && (
        <ConfirmDialog
          open={state.showConfirmDialog}
          title="Excluir notificação"
          question={`Deseja realmente excluir a notificação '${notification.title?.pt}'?`}
          handleConfirm={handleDeleteButton}
          handleClose={() => setState({ showConfirmDialog: false })}
          cancelButton
        />
      )}

      {state.showCancelDialog && (
        <ConfirmDialog
          open={state.showCancelDialog}
          title="Cancelar agendamento"
          question={`Deseja cancelar o agendamento da notificação '${notification.title?.pt}'?`}
          handleConfirm={handleCancelScheduled}
          handleClose={() => setState({ showCancelDialog: false })}
          confimButtonLabel="Cancelar agendamento"
          cancelButtonLabel="Voltar"
        />
      )}
    </>
  );
}
